import { ErrorMessage } from './../backend/his-backend.service';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { Notification, NotificationType } from '../../model/notification';
import { HisAuthService } from '../../../auth/service/his-auth.service';
import { HisUiService } from '../base/his-ui.service';
import { HisNavigationService } from '../base/his-navigation.service';


@Injectable({providedIn: 'root'})
export class HisNotificationService {
    private hisUiService = inject(HisUiService);
    hisAuthService = inject(HisAuthService);
    private hisNavigationService = inject(HisNavigationService);

    private subject = new BehaviorSubject<Notification>(undefined);

    // Benachrichtigungsmechanismus für FieldMessages
    private fieldMessages = new Subject<ErrorMessage>();
    public fieldMessages$ = this.fieldMessages.asObservable();

    public notifyFieldMessage( message: ErrorMessage ) {
        this.fieldMessages.next( message );
    }

    getNotification(): Observable<Notification> {
        return this.subject.asObservable();
    }

    public notify(notification: Notification) {
        this.notification(notification.type, notification.messages, notification.header, notification.timeOut);
    }

    success( message: string | string[], header: string, timeOut?: number ) {
        this.hisNavigationService.dirty = false;
        if ( !timeOut ) {
            timeOut = 4000;
        }
        if ( this.hisAuthService.hasAll( 'RIGHT_CS_SYS_USABILITY_SHOW_OCCASIONAL_USER' ) ) {
            timeOut = undefined;
        }
        this.notification( NotificationType.Success, message, header, timeOut );
    }

    error( message: string | string[], header: string, timeOut?: number ) {
        this.notification( NotificationType.Error, message, header, timeOut );
    }

    info( message: string | string[], header: string, timeOut?: number ) {
        this.notification( NotificationType.Info, message, header, timeOut );
    }

    warn( message: string | string[], header: string, timeOut?: number ) {
        this.notification( NotificationType.Warning, message, header, timeOut );
    }

    private notification(
        type: NotificationType,
        message: string | string[],
        header: string,
        timeOut?: number
    ) {
        // convert single elements to array with one element
        message = [].concat( message );

        // wenn in iFrame, dann Meldung weiter reichen an jsf und hier unterbinden
        if (window !== window.parent) {
            const messages = [];
            const notificationTypeLowerCase = NotificationType[type].toLowerCase();
            for (let m of message) {
                messages.push({
				                text: m,
				                type: notificationTypeLowerCase,
				                title: header});
            }
            const stringNotification = JSON.stringify(messages);
            window.parent.postMessage(stringNotification, window.location.origin );
            return;
        }

        this.subject.next( {
            type,
            messages: message,
            header,
            timeOut,
            state: 'new'
        } as Notification );
    }

    clear() {
        this.subject.next(undefined);
    }


    public errorI18nRes( messageKey: string, ...params: any[] ): void {
        this.error(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'fs.res.common.service.error' )
        );
    }

    public errorI18n( messageKey: string, ...params: any[] ): void {
        this.error(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'view.messages.error' )
        );
    }

    public warnI18nRes( messageKey: string, ...params: any[] ): void {
        this.warn(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'fs.res.common.service.warn' )
        );
    }

    public warnI18n( messageKey: string, ...params: any[] ): void {
        this.warn(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'view.messages.warning' )
        );
    }

    public infoI18nRes( messageKey: string, ...params: any[] ): void {
        this.info(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'fs.res.common.service.information' )
        );
    }

    public infoI18n( messageKey: string, ...params: any[] ): void {
        this.info(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'view.messages.information' )
        );
    }

    public successI18nRes( messageKey: string, ...params: any[] ): void {
        this.success(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'fs.res.common.success' )
        );
    }

    public successI18n( messageKey: string, ...params: any[] ): void {
        this.success(
            this.hisUiService.translate( messageKey, ...params ),
            this.hisUiService.translate( 'view.messages.success' )
        );
    }

    public deleteSuccessI18nRes(): void {
        this.success(
            this.hisUiService.translate( 'view.success.delete' ),
            this.hisUiService.translate( 'fs.res.common.confirmation.delete' )
        );
    }

    public deleteSuccessI18n(): void {
        this.success(
            this.hisUiService.translate( 'view.success.delete' ),
            this.hisUiService.translate( 'fs.res.common.confirmation.delete' )
        );
    }

}
